"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { HiArrowRight } from "@/components/icons";

interface ResourceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  count: number | null;
  countLabel: string | null;
  featured: Array<{ title: string; href: string }>;
}

export default function ResourceCard({
  icon,
  title,
  description,
  href,
  count,
  countLabel,
  featured,
}: ResourceCardProps) {
  const router = useRouter();

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only navigate if clicking on the card itself, not on links
    if ((e.target as HTMLElement).closest("a")) {
      return;
    }
    router.push(href);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      if ((e.target as HTMLElement).closest("a")) {
        return;
      }
      e.preventDefault();
      router.push(href);
    }
  };

  return (
    <div
      className="group rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm p-6 flex flex-col hover:border-primary/20 transition-all duration-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="group"
      tabIndex={0}
      aria-label={`${title} — ${description}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
          {icon}
        </div>
        {count !== null && (
          <span className="text-xs font-mono font-bold text-muted-foreground bg-muted px-2 py-1 rounded-full">
            {count} {countLabel}
          </span>
        )}
      </div>

      <h2 className="text-xl font-bold mb-2 tracking-tight group-hover:text-primary transition-colors">
        {title}
      </h2>
      <p className="text-sm text-muted-foreground mb-5 flex-1">{description}</p>

      {featured.length > 0 && (
        <div className="space-y-1.5 mb-5 border-t border-border/50 pt-4">
          {featured.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block text-sm text-foreground hover:text-primary transition-colors truncate relative z-10"
            >
              <span className="text-primary/40 mr-1.5">→</span>
              {item.title}
            </Link>
          ))}
        </div>
      )}

      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary mt-auto">
        View All{" "}
        <HiArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </div>
  );
}
